import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { LinkAnalysisService } from '../../../services/link-analysis/link-analysis.service';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-link-analysis-search',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, SidebarComponent, CreditsLabelPipe],
  templateUrl: './link-analysis-search.component.html',
  styleUrls: ['./link-analysis-search.component.css'],
})
export class LinkAnalysisSearchComponent implements OnInit {
  activeWorkspace: 'automated' | 'drill_down' = 'drill_down';
  rootType: 'phone' | 'email' | 'username' = 'phone';
  rootValue = '';
  /** Automated max depth: 1 = root ring only, 2 = default, 3 = deepest auto. */
  maxLayer = 2;
  readonly maxLayerOptions = [
    { value: 1, label: '1 layer', hint: 'Root result only (fastest)' },
    { value: 2, label: '2 layers', hint: 'Default — email/phone/image expand once' },
    { value: 3, label: '3 layers', hint: 'Deeper auto-expand (slower)' },
  ];
  drillDownQueryType: 'phone' | 'email' | 'username' = 'phone';
  drillDownQueryValue = '';
  photoUrlsText = '';
  loading = false;
  loadingHistory = false;
  recentInvestigations: any[] = [];
  availableCredits: number | null = null;
  creditsLoading = true;
  creditCosts: Record<string, number> | null = null;
  hasActiveSubscription = false;
  isServiceDisabled = false;
  subscriptionHint = 'An active subscription is required to run link analysis.';
  readonly phonePlaceholder = '+910000000000';
  readonly usernamePlaceholder = 'username or handle';

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private linkAnalysisService: LinkAnalysisService,
    private searchServiceStatusService: SearchServiceStatusService,
    private userAccountService: UserAccountService,
  ) { }

  ngOnInit(): void {
    this.searchServiceStatusService.creditCosts$.subscribe((creditCosts) => {
      this.creditCosts = creditCosts;
    });

    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = searchServices.link_analysis === false;
      }
    });

    this.searchServiceStatusService.refreshSearchServices();
    this.loadRecentInvestigations();
    this.loadCredits();
  }

  setRootType(type: 'phone' | 'email' | 'username'): void {
    this.rootType = type;
  }

  get automatedInputType(): string {
    if (this.rootType === 'email') return 'email';
    if (this.rootType === 'phone') return 'tel';
    return 'text';
  }

  get automatedInputPlaceholder(): string {
    if (this.rootType === 'email') return 'person@example.com';
    if (this.rootType === 'phone') return this.phonePlaceholder;
    return this.usernamePlaceholder;
  }

  get automatedInputLabel(): string {
    if (this.rootType === 'email') return 'Email address';
    if (this.rootType === 'phone') return 'Phone number';
    return 'Username';
  }

  get automatedInputIcon(): string {
    if (this.rootType === 'email') return 'fa-envelope';
    if (this.rootType === 'phone') return 'fa-mobile-alt';
    return 'fa-user';
  }

  setDrillDownQueryType(type: 'phone' | 'email' | 'username'): void {
    this.drillDownQueryType = type;
  }

  setWorkspace(mode: 'automated' | 'drill_down'): void {
    this.activeWorkspace = mode;
  }

  onBuyCreditsButtonClick(): void {
    this.router.navigate(['/plan-pricing']);
  }

  get drillDownInputType(): string {
    if (this.drillDownQueryType === 'email') return 'email';
    if (this.drillDownQueryType === 'phone') return 'tel';
    return 'text';
  }

  get drillDownInputPlaceholder(): string {
    if (this.drillDownQueryType === 'email') return 'person@example.com';
    if (this.drillDownQueryType === 'phone') return this.phonePlaceholder;
    return 'username or handle';
  }

  get drillDownInputLabel(): string {
    if (this.drillDownQueryType === 'email') return 'Email address';
    if (this.drillDownQueryType === 'phone') return 'Phone number';
    return 'Username';
  }

  get drillDownInputIcon(): string {
    if (this.drillDownQueryType === 'email') return 'fa-envelope';
    if (this.drillDownQueryType === 'phone') return 'fa-mobile-alt';
    return 'fa-user';
  }

  get normalizedPhotoRefs() {
    return this.photoUrlsText
      .split('\n')
      .map((value) => value.trim())
      .filter(Boolean)
      .map((url) => ({ image_url: url }));
  }

  get estimatedCreditCost(): number | null {
    if (!this.creditCosts) return null;
    if (this.activeWorkspace === 'drill_down') {
      return (
        this.searchServiceStatusService.resolveCreditCost(this.creditCosts, 'osintSearchCost') ?? 0
      );
    }
    const osint =
      this.searchServiceStatusService.resolveCreditCost(this.creditCosts, 'osintSearchCost') ?? 0;
    const leak =
      this.searchServiceStatusService.resolveCreditCost(this.creditCosts, 'leakSearchCost') ?? 0;
    const face =
      this.searchServiceStatusService.resolveCreditCost(this.creditCosts, 'searchCreditCount') ?? 0;
    return osint + leak + face * this.normalizedPhotoRefs.length;
  }

  get estimatedCreditLabel(): string {
    if (!this.creditCosts) return 'Uses configured credits';
    if (this.activeWorkspace === 'drill_down') {
      return `${this.estimatedCreditCost ?? 0} credits for first OSINT search`;
    }
    return `${this.estimatedCreditCost ?? 0} credits`;
  }

  get runCostLabel(): string {
    if (this.activeWorkspace === 'drill_down') {
      if (!this.creditCosts) return 'Per action';
      return `${this.estimatedCreditCost ?? 0} credits`;
    }
    if (!this.creditCosts) return 'Uses configured credits';
    return `${this.estimatedCreditCost ?? 0} credits`;
  }

  get loadingTitle(): string {
    return this.activeWorkspace === 'drill_down'
      ? 'Starting drill-down search'
      : 'Starting link analysis';
  }

  get loadingDetail(): string {
    return this.activeWorkspace === 'drill_down'
      ? 'Creating workspace and running the first OSINT search…'
      : 'Building the investigation graph…';
  }

  loadRecentInvestigations(): void {
    this.loadingHistory = true;
    this.linkAnalysisService.getHistory(1, 10).subscribe({
      next: (response) => {
        this.recentInvestigations = response?.data?.data_list || [];
        this.loadingHistory = false;
      },
      error: () => {
        this.loadingHistory = false;
      },
    });
  }

  loadCredits(): void {
    this.creditsLoading = true;
    this.userAccountService.getSubscriptionDetails().subscribe({
      next: (response) => {
        const currentPlan = response?.data?.currentPlan || null;
        this.hasActiveSubscription = !!currentPlan && !currentPlan?.isExpired;
        const isUnlimited = this.userAccountService.pickIsUnlimited(response);
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.subscriptionHint = !this.hasActiveSubscription
          ? 'No active subscription found. Please activate or assign a credit plan first.'
          : isUnlimited
            ? 'Unlimited searches until your license expires. Purchased credits stay queued and activate after expiry.'
            : (this.availableCredits ?? 0) > 0
              ? 'OSINT, leak, and optional user-photo face-search runs. Automatic face enrichment from top raw search-result images is included.'
              : 'Your active subscription has no remaining credits for this run.';
        this.creditsLoading = false;
      },
      error: (error) => {
        this.hasActiveSubscription = false;
        this.availableCredits = 0;
        this.subscriptionHint =
          error?.error?.message_key ||
          error?.error?.message ||
          'Unable to verify active subscription details right now.';
        this.creditsLoading = false;
      },
    });
  }

  get canStartInvestigation(): boolean {
    if (this.loading || this.creditsLoading || !this.hasActiveSubscription) return false;
    if (this.activeWorkspace === 'drill_down') {
      if (!this.drillDownQueryValue.trim()) return false;
      if (this.estimatedCreditCost === null) return (this.availableCredits ?? 0) > 0;
      return (this.availableCredits ?? 0) >= this.estimatedCreditCost;
    }
    if (!this.rootValue.trim()) return false;
    if (this.estimatedCreditCost === null) return (this.availableCredits ?? 0) > 0;
    return (this.availableCredits ?? 0) >= this.estimatedCreditCost;
  }

  isDrillDownInvestigation(item: any): boolean {
    return item?.mode === 'drill_down';
  }

  getHistoryModeLabel(item: any): string {
    return this.isDrillDownInvestigation(item)
      ? 'Drill-down Search'
      : 'Automated Link Analysis';
  }

  getHistoryRootType(item: any): string {
    if (this.isDrillDownInvestigation(item)) {
      const rootType =
        item?.root_type ||
        item?.summary?.rootType ||
        item?.graph_payload?.summary?.rootType;
      if (rootType && rootType !== 'drill_down') {
        return String(rootType).toUpperCase();
      }
      return 'DRILL-DOWN';
    }
    return (item?.root_type || 'link analysis').toUpperCase();
  }

  getHistoryRootValue(item: any): string {
    if (this.isDrillDownInvestigation(item)) {
      return (
        item?.root_value ||
        item?.summary?.rootValue ||
        item?.graph_payload?.summary?.rootValue ||
        'Drill-down investigation'
      );
    }
    return item?.root_value || 'Automated investigation';
  }

  openInvestigation(linkAnalysisId: string): void {
    this.router.navigate(['/link-analysis/result', linkAnalysisId]);
  }

  getStatusLabel(item: any): string {
    const status = (item?.status || '').toLowerCase();
    if (status === 'completed' || status === 'complete') return 'Completed';
    if (status === 'processing' || status === 'pending') return 'Processing';
    if (status === 'failed' || status === 'error') return 'Failed';
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
  }

  isCompletedStatus(item: any): boolean {
    const status = (item?.status || '').toLowerCase();
    return status === 'completed' || status === 'complete';
  }

  isProcessingStatus(item: any): boolean {
    const status = (item?.status || '').toLowerCase();
    return status === 'processing' || status === 'pending';
  }

  getPhotoInputLabel(item: any): string {
    const count = item?.photo_refs?.length || 0;
    return `${count} input${count === 1 ? '' : 's'}`;
  }

  getNodeCount(item: any): number {
    return item?.summary?.nodeCount || item?.graph_payload?.summary?.nodeCount || 0;
  }

  createInvestigation(): void {
    if (this.isServiceDisabled) {
      this.toastr.error('Link analysis is currently unavailable');
      return;
    }
    const trimmedValue = this.rootValue.trim();
    if (!trimmedValue) {
      this.toastr.error(
        this.rootType === 'phone'
          ? 'Please enter a phone number.'
          : this.rootType === 'email'
            ? 'Please enter an email address.'
            : 'Please enter a username.',
      );
      return;
    }

    this.loading = true;
    this.linkAnalysisService
      .investigate({
        root_type: this.rootType,
        root_value: trimmedValue,
        photo_refs: this.normalizedPhotoRefs,
        max_layer: this.maxLayer,
      })
      .subscribe({
        next: (response) => {
          const id = response?.data?.link_analysis_id;
          if (!id) {
            this.loading = false;
            this.toastr.error('The investigation started, but no result id came back.');
            return;
          }
          this.toastr.success('Investigation started. We are building the graph now.');
          this.router.navigate(['/link-analysis/result', id]);
        },
        error: (error) => {
          this.loading = false;
          const message =
            error?.error?.message_key ||
            error?.error?.message ||
            error?.error?.data?.message ||
            'Unable to start link analysis investigation.';
          this.toastr.error(message);
          this.loadCredits();
        },
      });
  }

  createDrillDownInvestigation(): void {
    if (this.isServiceDisabled) {
      this.toastr.error('Link analysis is currently unavailable');
      return;
    }
    const trimmedValue = this.drillDownQueryValue.trim();
    if (!trimmedValue) {
      this.toastr.error(
        this.drillDownQueryType === 'phone'
          ? 'Please enter a phone number.'
          : this.drillDownQueryType === 'email'
            ? 'Please enter an email address.'
            : 'Please enter a username.',
      );
      return;
    }

    if (!this.canStartInvestigation) return;

    this.loading = true;
    this.linkAnalysisService
      .createDrillDownWorkspace({
        query_type: this.drillDownQueryType,
        query_value: trimmedValue,
        action_type: 'osint_search',
      })
      .subscribe({
        next: (response) => {
          const id = response?.data?.link_analysis_id;
          if (!id) {
            this.loading = false;
            this.toastr.error('The drill-down search started, but no result id came back.');
            return;
          }
          this.toastr.success('Drill-down search started. We are building the graph now.');
          this.router.navigate(['/link-analysis/result', id]);
        },
        error: (error) => {
          this.loading = false;
          const message =
            error?.error?.message_key ||
            error?.error?.message ||
            error?.error?.data?.message ||
            'Unable to start drill-down search.';
          this.toastr.error(message);
          this.loadCredits();
        },
      });
  }
}
